import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import * as d3 from 'd3';
import { dom, library } from '@fortawesome/fontawesome-svg-core';
import {
  faCrosshairs,
  faUser,
  faUserFriends,
} from '@fortawesome/pro-solid-svg-icons';

import { svgWithJs, variables } from '../variables';

interface DataPoint {
  value: number;
  target: number;
}

interface Dimensions {
  width: number;
  height: number;
  margin: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  boundedWidth: number;
  boundedHeight: number;
}

interface DOMElements {
  svg: d3.Selection<any, unknown, null, undefined>;
  bounds: d3.Selection<SVGGElement, unknown, null, undefined>;
}

type AllowedIcons = '' | 'target' | 'participants' | 'individual';

const gray = '#f2f2f2';
const darkgray = '#4a4a4a';
const green = '#8dc63f';
const orange = '#e36f1e';
const yellow = '#fdbb30';

// Scale of this component is 0 to 1 decimal points.

@customElement('apt-target-chart')
export class TargetChart extends LitElement {
  @property({ type: Array })
  data: Array<DataPoint> = [];

  @property({ type: String })
  icon: AllowedIcons = 'target';

  @property({ type: String })
  middleText = '';

  @property({ type: Array })
  colorValueRange = [orange, yellow, green];

  @property({ type: String })
  operator = 'and';

  @property({ type: Array })
  colorrange: Array<number> = [0.33, 0.8, 1];

  @property({ type: Number })
  width = 50;

  @property({ type: Number })
  height = 50;

  @property({ type: String })
  emptyColor = gray;

  @property({ type: String }) strokeColor = '#ffffff100';
  @property({ type: Number }) strokeWidth = 0;

  static override get styles() {
    return [
      variables,
      svgWithJs,
      css`
        :host {
          height: 50px;
        }
        .middle-text-section {
          display: block;
        }
      `,
    ];
  }

  singleInnerRadiusScale = 50 / 16;
  singleOuterRadiusScale = 50 / 24;

  doubleInnerRadiusScale1 = 50 / 20;
  doubleOuterRadiusScale1 = 50 / 24;

  doubleInnerRadiusScale2 = 50 / 15;
  doubleOuterRadiusScale2 = 50 / 19;
  constructor() {
    super();

    // These are our only icons.
    library.add(faCrosshairs, faUserFriends, faUser);
  }

  // Originally this was simply a structure that I indexed into via the length of the data I had
  // and the index of the particular data item. But TypeScript said, "No. You can't do that. You cannot
  // use numeric keys in an object." As is so often the case, I guess they should have told
  // the ECMAScript people that allowing that to work just fine wasn't OK with them. It seems as
  // though the JavaScript people are just implementing whatever they like without asking TypeScript's
  // permission.
  chartSpecs(numDataPoints: number, index: number) {
    const defaultSpecs = {
      cornerRadius: 0,
      innerRadius: 0,
      outerRadius: 0,
    };

    // If you only have one data point, it uses the first section, if you have two, it uses the second.
    // These can be adjusted to alter the width and position of the circle(s) drawn.
    //
    // Note: All of these could be re-expressed in the form of calculations based upon the size
    // of the graph to be drawn, but they're hard-coded at the moment. No one has asked for a scalable
    // version of this chart.
    switch (numDataPoints) {
      case 1:
        return {
          cornerRadius: 4,
          innerRadius: this.width / this.singleInnerRadiusScale,
          outerRadius: this.width / this.singleOuterRadiusScale,
        };
      case 2:
        switch (index) {
          case 0:
            return {
              cornerRadius: 2,
              innerRadius: this.width / this.doubleInnerRadiusScale1,
              outerRadius: this.width / this.doubleOuterRadiusScale1,
            };
          case 1:
            return {
              cornerRadius: 2,
              innerRadius: this.width / this.doubleInnerRadiusScale2,
              outerRadius: this.width / this.doubleOuterRadiusScale2,
            };
          default:
            return defaultSpecs;
        }
      default:
        return defaultSpecs;
    }
  }

  createDimensions() {
    // I'm starting this off with a relatively small margin all the way around. I may
    // increase it later if needed.
    const dimensions: Dimensions = {
      width: this.width,
      height: this.height,
      margin: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
      boundedWidth: 0,
      boundedHeight: 0,
    };

    dimensions.boundedWidth =
      dimensions.width - dimensions.margin.left - dimensions.margin.right;
    dimensions.boundedHeight =
      dimensions.height - dimensions.margin.top - dimensions.margin.bottom;

    return dimensions;
  }

  createDomElements(dimensions: Dimensions): DOMElements {
    // This is the outermost container for the chart. Just in case we already have SVG elements out
    // there (say from a previous rendering), remove it first.
    d3.select(this.shadowRoot as unknown as Element)
      .selectChildren('svg#targetchart')
      .remove();

    const svg = d3
      .select(this.shadowRoot as unknown as Element)
      .append('svg')
      .attr('id', 'targetchart')
      .attr('width', dimensions.width)
      .attr('height', dimensions.height);

    // But there's an inner container which allows us to have margin space for axes, labels, etc.
    const bounds = svg
      .append('g')
      .style(
        'transform',
        `translate(${dimensions.margin.left}px, ${dimensions.margin.top}px)`,
      );

    if (this.middleText) {
      bounds
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', '.25em')
        .attr('class', 'middle-text-section')
        .attr('data-test-selector', 'middle-text')
        .attr(
          'transform',
          `translate(${this.width / 2},${this.width / 2}) scale(0.75, 0.75)`,
        )
        .text(this.middleText);
    }

    const iconName = this.getIconName(this.icon);

    // data-fa-symbol is the name of the symbol that we will refer to later to draw an instance of the icon.
    // The actual class must refer to the name of the symbol that we're importing and plan to use there. They
    // do not have to be the same. For example:
    //   <i data-fa-symbol="picture-taker" class="fas fa-camera"></i>;
    //
    // Here I always use the name "icon" but swap out the actual font symbol used.
    d3.select(this.shadowRoot as unknown as Element)
      .append('i')
      .attr('data-fa-symbol', 'icon')
      .attr('class', `fas ${iconName}`);

    return { svg, bounds };
  }

  createScales(
    data: Array<DataPoint>,
    dimensions: Dimensions,
  ): {
    radiansScale: d3.ScaleLinear<number, number>;
    colorScale: d3.ScaleThreshold<number, number>;
  } {
    this.colorrange = [0.9, 0.97, 1];

    const radiansScale = d3
      .scaleLinear()
      .domain([0, 1])
      .range([-90 * (Math.PI / 180), (360 - 90) * (Math.PI / 180)]);

    // The numbers in the color scale indicate the levels at which it switches from one color to the next.
    // Right now the first color would start at zero and end at the first value, the second
    // would begin immediately after and end at the second value, and the third would begin
    // after the second and end at one.
    const colorScale = d3
      .scaleThreshold()
      .domain(this.colorrange)
      // @ts-ignore
      .range(this.colorValueRange);

    return { radiansScale, colorScale };
  }

  drawData(
    { bounds }: DOMElements,
    data: Array<DataPoint>,
    dimensions: Dimensions,
    {
      radiansScale,
      colorScale,
    }: {
      radiansScale: d3.ScaleLinear<number, number>;
      colorScale: d3.ScaleThreshold<number, number>;
    },
  ) {
    let colorValue = 0;
    if (this.operator?.toLowerCase() === 'or') {
      colorValue =
        d3.max(data, (item) => Math.round(item.value * 100) / 100 || 0) ?? 0;
    } else {
      colorValue =
        d3.min(data, (item) => Math.round(item.value * 100) / 100 || 0) ?? 0;
    }

    // Draw a background gray circle onto which we'll overlay the colored circle that shows our value.
    const completeCircle = d3.arc();

    bounds
      .selectAll('g')
      .data(data)
      .enter()
      .append('path')
      .attr(
        'transform',
        `translate(${dimensions.width / 2 - 1}, ${dimensions.height / 2 - 1})`,
      )
      .attr('d', (d: DataPoint, i: number) =>
        completeCircle({
          innerRadius: this.chartSpecs(data.length, i).innerRadius,
          outerRadius: this.chartSpecs(data.length, i).outerRadius,
          startAngle: 0,
          endAngle: 2 * Math.PI,
        }),
      )
      .attr('stroke', this.strokeColor)
      .style('stroke-width', this.strokeWidth)
      .attr('fill', this.emptyColor);

    // Now we'll draw the actual value. The corner radius is about rounding the ends of the arcs
    // we're drawing so they look like the original drawings from Seth Weaver. Note: cornerRadius is
    // unlike the other parameters and cannot be passed in as part of the object like we do below for
    // innerRadius, outerRadius, and endAngle.
    const arc = d3
      .arc()
      .cornerRadius(this.chartSpecs(data.length, 0).cornerRadius);

    bounds
      .selectAll('g')
      .data(data)
      .enter()
      .append('path')
      .attr(
        'transform',
        `translate(${dimensions.width / 2 - 1}, ${dimensions.height / 2 - 1})`,
      )
      .attr('d', (d, i) =>
        arc({
          innerRadius: this.chartSpecs(data.length, i).innerRadius,
          outerRadius: this.chartSpecs(data.length, i).outerRadius,
          startAngle: radiansScale(0),
          endAngle: radiansScale(d.value || 0),
        }),
      )
      .attr('fill', colorScale(colorValue));
  }

  drawPeripheralsBeforeData(
    { bounds }: DOMElements,
    data: Array<DataPoint>,
    dimensions: Dimensions,
    { },
  ) {
    // Add a backdrop to the graph.
    bounds
      .append('rect')
      .attr('width', dimensions.boundedWidth)
      .attr('height', dimensions.boundedHeight)
      .attr('fill', 'transparent');
  }

  drawPeripheralsAfterData(
    { svg, bounds }: DOMElements,
    data: Array<DataPoint>,
    dimensions: Dimensions,
    { },
    icon: AllowedIcons,
  ) {
    // Draw an SVG icon in the middle of the graph.
    if (data && data.length > 0) {
      bounds
        .append('svg')
        .attr('class', 'icon')
        .append('use')
        .attr('xlink:href', `#icon`)
        .attr(
          'transform',
          `translate(${this.width / this.singleInnerRadiusScale},${this.width / this.singleInnerRadiusScale
          }) scale(0.33, 0.33)`,
        )
        .attr('fill', darkgray);
    }
  }

  generateChart(data: Array<DataPoint>, icon: AllowedIcons) {
    const dimensions = this.createDimensions();
    if (this.shadowRoot) {
      const domElements = this.createDomElements(dimensions);
      const scales = this.createScales(data, dimensions);
      this.drawPeripheralsBeforeData(domElements, data, dimensions, scales);
      this.drawData(domElements, data, dimensions, scales);
      this.drawPeripheralsAfterData(
        domElements,
        data,
        dimensions,
        scales,
        icon,
      );
    }
  }

  override render() {
    this.generateChart(this.data, this.icon);

    return html`<div></div>`;
  }

  override firstUpdated(): void {
    // @ts-ignore The dom.watch() TS definition indicates no parameters for this. However, when
    // you actually dig into the code, you see that it looks at the arguments after it gets into
    // the code and tries to pull some out if there aren't any (it even supplies an empty object
    // for options if you don't supply one yourself).
    //
    // Note: We have to wait for the first update for the shadow DOM to even be created. Starting
    // the watch now will cause the <i> we inserted to be replaced with an SVG symbol we can reference
    // from out D3.js code.
    dom.watch({
      autoReplaceSvgRoot: this.shadowRoot,
      observeMutationsRoot: this.shadowRoot,
    });
  }

  getIconName(icon: AllowedIcons): string {
    switch (icon) {
      case 'participants':
        return 'fa-user-friends';
      case 'individual':
        return 'fa-user';
      case 'target':
        return 'fa-crosshairs';
      default:
        return '';
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'apt-target-chart': TargetChart;
  }
}
